import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { getApiErrorMessage, getMaterialItems, getMaterialQrUrl } from "@/lib/api";

const TAMANOS = {
  normal: { label: "Normal", grid: "grid-cols-3", qr: "w-36 h-36" },
  pequena: { label: "Pequeña", grid: "grid-cols-4", qr: "w-24 h-24" },
};

/** Hoja para imprimir las etiquetas QR de todo el material. */
export default function MaterialEtiquetasPage() {
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [excluidos, setExcluidos] = useState(() => new Set());
  const [tamano, setTamano] = useState("normal");

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate("/login", { replace: true });
    else if (user.permissions?.material === false) navigate("/candidatos", { replace: true });
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user || user.permissions?.material === false) return;
    getMaterialItems()
      .then((d) => setItems((d.items || []).filter((i) => i.etiqueta_url)))
      .catch((e) => setError(getApiErrorMessage(e, "No se pudo cargar el material")));
  }, [user]);

  if (!items) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-white">
        {error ? <p className="text-sm text-red-600">{error}</p> : <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />}
      </div>
    );
  }

  const t = TAMANOS[tamano];
  const imprimibles = items.filter((i) => !excluidos.has(i.id));
  const toggle = (id) =>
    setExcluidos((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <div className="min-h-dvh bg-neutral-100 print:bg-white">
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 border-b bg-background px-6 py-3 shadow-sm print:hidden">
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate("/material")}>
          <ArrowLeft className="h-4 w-4" /> Material
        </Button>
        <h1 className="font-semibold">Etiquetas QR</h1>
        <span className="text-sm text-muted-foreground">
          {imprimibles.length} de {items.length} · toca una etiqueta para excluirla
        </span>
        <div className="ml-auto flex rounded-lg border bg-muted p-0.5">
          {Object.entries(TAMANOS).map(([id, v]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTamano(id)}
              className={cn("rounded-md px-3 py-1 text-xs font-medium", tamano === id ? "bg-background shadow-sm" : "text-muted-foreground")}
            >
              {v.label}
            </button>
          ))}
        </div>
        <Button onClick={() => window.print()} className="gap-1.5" disabled={!imprimibles.length}>
          <Printer className="h-4 w-4" /> Imprimir
        </Button>
      </div>

      <div className={cn("mx-auto grid max-w-5xl gap-4 p-6 print:max-w-none print:gap-3 print:p-0", t.grid)}>
        {items.map((item) => {
          const fuera = excluidos.has(item.id);
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => toggle(item.id)}
              className={cn(
                "relative flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-neutral-300 bg-white p-4 text-center break-inside-avoid",
                fuera && "opacity-30 print:hidden"
              )}
            >
              <span className="absolute right-2 top-2 print:hidden">
                <Checkbox checked={!fuera} tabIndex={-1} />
              </span>
              <img src={getMaterialQrUrl(item.id, { version: item.etiqueta_url })} alt="" className={t.qr} />
              <span className="text-sm font-bold leading-tight text-neutral-900">{item.nombre}</span>
              <span className="text-[10px] leading-tight text-neutral-500">Escanea para llevártelo o devolverlo</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
